import React, { useState, useEffect } from 'react';
import { TrendingUp, Download, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { csvCell } from '../utils/csvSafe';
import { NavBar } from './NavBar';

interface InboundRow {
  facility_code: string;
  facility_name: string;
  material_code: string;
  material_name: string;
  total_tons: string;
}

interface Filters {
  year: string;
  month: string;
  facility: string;
}

const MONTHS = [
  { value: '1',  label: 'January'   }, { value: '2',  label: 'February'  },
  { value: '3',  label: 'March'     }, { value: '4',  label: 'April'     },
  { value: '5',  label: 'May'       }, { value: '6',  label: 'June'      },
  { value: '7',  label: 'July'      }, { value: '8',  label: 'August'    },
  { value: '9',  label: 'September' }, { value: '10', label: 'October'   },
  { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];

const now           = new Date();
const CURRENT_YEAR  = String(now.getFullYear());
const CURRENT_MONTH = String(now.getMonth() + 1);

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function fmtTons(s: string | undefined): string {
  return toNum(s).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function InboundSummary() {
  const { getToken } = useAuth();

  const [filters, setFilters] = useState<Filters>({
    year: CURRENT_YEAR, month: CURRENT_MONTH, facility: '',
  });

  const [facilities, setFacilities] = useState<{ facility_code: string; facility_name: string }[]>([]);
  const [data,       setData]       = useState<InboundRow[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [hasLoaded,  setHasLoaded]  = useState(false);

  const API_URL = process.env.REACT_APP_API_URL ?? '';

  // Load facility dropdown on mount
  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}/reports/generate`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ report_id: 'lookup.facilities', params: {} }),
        });
        const result = await res.json();
        setFacilities(result.data ?? []);
      } catch (e) {
        console.error('Failed to load facilities:', e);
      }
    };
    load();
  }, []);

  const fetchReport = async () => {
    if (!filters.year || !filters.month) return;
    setLoading(true);
    setError(null);
    try {
      const token  = await getToken();
      const params: Record<string, string> = { year: filters.year, month: filters.month };
      if (filters.facility) params.facility = filters.facility;

      const res = await fetch(`${API_URL}/reports/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: 'dashboard.inbound', params }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result.data ?? []);
      setHasLoaded(true);
    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const monthLabel = MONTHS.find(m => m.value === filters.month)?.label ?? filters.month;
    const headers    = ['Year', 'Month', 'Facility', 'Material Code', 'Material Name', 'Total Tons'];
    const rows       = data.map(row => [
      filters.year, monthLabel, row.facility_name,
      row.material_code, row.material_name || row.material_code, row.total_tons,
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => csvCell(c)).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `inbound-summary-${filters.year}-${filters.month.padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalTons     = data.reduce((s, r) => s + toNum(r.total_tons), 0);
  const facilityCount = new Set(data.map(r => r.facility_code)).size;
  const materialCount = new Set(data.map(r => r.material_code)).size;

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-green-600 rounded-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Inbound Summary</h1>
              <p className="text-sm text-gray-500">Total inbound tons by facility and material</p>
            </div>
          </div>
          {hasLoaded && data.length > 0 && (
            <button onClick={exportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors">
              <Download className="w-4 h-4" /><span>Export CSV</span>
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year <span className="text-red-500">*</span></label>
              <input type="number" value={filters.year} min="2020" max={Number(CURRENT_YEAR)}
                onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Month <span className="text-red-500">*</span></label>
              <select value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Facility</label>
              <select value={filters.facility} onChange={e => setFilters(f => ({ ...f, facility: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">All Facilities</option>
                {facilities.map(fc => <option key={fc.facility_code} value={fc.facility_code}>{fc.facility_name || fc.facility_code}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={fetchReport} disabled={loading || !filters.year || !filters.month}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run Report'}</span>
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Failed to load report</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Summary cards */}
        {hasLoaded && data.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Inbound', value: totalTons.toLocaleString('en-US', { maximumFractionDigits: 1 }) + ' tons', color: 'text-green-600' },
              { label: 'Facilities',   value: String(facilityCount), color: 'text-blue-600' },
              { label: 'Materials',    value: String(materialCount), color: 'text-purple-600' },
            ].map(card => (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        {hasLoaded && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {data.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-25" />
                <p className="text-sm font-medium">No data found for the selected filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {['Facility', 'Material Code', 'Material Name', 'Total Tons'].map(col => (
                        <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{row.facility_name}</td>
                        <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">{row.material_code}</td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.material_name || row.material_code}</td>
                        <td className="px-4 py-3 text-right text-gray-800 tabular-nums font-medium">{fmtTons(row.total_tons)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                      <td className="px-4 py-3 text-gray-900" colSpan={3}>Total</td>
                      <td className="px-4 py-3 text-right text-gray-900 tabular-nums">
                        {totalTons.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {!hasLoaded && !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-20">
            <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-base font-medium text-gray-500">Select filters and click Run Report</p>
            <p className="text-sm text-gray-400 mt-1">Year and Month are required</p>
          </div>
        )}
      </main>
    </div>
  );
}
